import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Client, Conversation, CreateConversationOptions, Paginator } from '@twilio/conversations'
import notificationSound from 'assets/sounds/sound-message.mp3'
import useSound from 'use-sound'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

type TwillioError = {
  terminal: boolean
  message: string
  httpStatusCode?: number
  errorCode?: number
}
type UseTwilioConversations = {
  isReady: boolean
  twilioClient: Client | null
  createConversation: (options: CreateConversationOptions) => Promise<Conversation | undefined>
  conversations: Conversation[] | undefined
  unreadMessagesTotal: number
  conversationsUnreadMessagesMap: Map<string, number>
  deleteConversation: (conversation: Conversation) => Promise<void>
  updateConversationFriendlyName: (conversation: Conversation, friendlyName: string) => Promise<void>
  conversationsLoading: boolean
  error: TwillioError | null
  setConversationsUnreadMessagesMap: React.Dispatch<React.SetStateAction<Map<string, number>>>
}

const useTwilioConversations = (chatToken: string | null): UseTwilioConversations => {
  const twilioClient = useRef<Client | null>(null)

  const [play] = useSound(notificationSound)
  const [isReady, setIsReady] = useState(false)
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const [conversations, setConversations] = useState<undefined | Conversation[]>([])
  const [conversationsUnreadMessagesMap, setConversationsUnreadMessagesMap] = useState(new Map<string, number>())
  const [error, setError] = useState<TwillioError | null>(null)

  const unreadMessagesTotal = useMemo(() => {
    let total = 0

    if (conversations?.length === 0 || conversationsUnreadMessagesMap.size === 0) return total
    conversationsUnreadMessagesMap.forEach(value => {
      total += value
    })

    return total
  }, [conversations, conversationsUnreadMessagesMap])

  const previousUnreadMessagesTotal = useRef(unreadMessagesTotal)

  useEffect(() => {
    if (previousUnreadMessagesTotal.current < unreadMessagesTotal) {
      play()
      previousUnreadMessagesTotal.current = unreadMessagesTotal
    }
  }, [unreadMessagesTotal, play])

  useEffect(() => {
    if (!chatToken) return

    twilioClient.current = new Client(chatToken)

    twilioClient.current.on('connectionStateChanged', state => {
      if (state === 'connected') {
        setIsReady(true)
        setError(null)
      } else {
        setIsReady(false)
      }
    })
  }, [chatToken])

  const getAllConversations = useCallback(async (cursor: Paginator<Conversation> | undefined, data?: Conversation[]): Promise<Conversation[]> => {
    const convos = [...(data ?? []), ...(cursor?.items ?? [])]

    if (!cursor?.hasNextPage) return convos

    const nextCursor = await cursor.nextPage()
    return getAllConversations(nextCursor, convos)
  }, [])

  const getTwilioConversations = useCallback(async () => {
    if (!isReady) return
    setConversationsLoading(true)
    const cursor = await twilioClient.current?.getSubscribedConversations()
    const convos = await getAllConversations(cursor)

    if (convos) {
      setConversations(convos)
    }
    setConversationsLoading(false)
  }, [getAllConversations, isReady])

  const createConversation = useCallback(async ({ attributes, friendlyName }: CreateConversationOptions) => {
    const createConversation = await twilioClient.current?.createConversation({ attributes, friendlyName })

    return createConversation
  }, [])

  const deleteConversation = useCallback(async (conversation: Conversation) => {
    await conversation.delete()
  }, [])

  const updateConversationFriendlyName = useCallback(async (conversation: Conversation, friendlyName: string) => {
    await conversation.updateFriendlyName(friendlyName)
  }, [])

  const getConversationUnredMessagesCount = useCallback(async (conversation: Conversation) => {
    const unreadCount = await conversation.getUnreadMessagesCount()
    const totalMessageCount = await conversation.getMessagesCount()
    return unreadCount ?? totalMessageCount
  }, [])
  useEffect(() => {
    if (!isReady) return
    ;(async () => {
      await getTwilioConversations()
      if (Capacitor.isNativePlatform()) {
        App.addListener('appStateChange', async state => {
          if (state.isActive) {
            await getTwilioConversations()
          }
        })
      }
    })()
  }, [isReady, getTwilioConversations])

  // event subscribtions for conversation
  useEffect(() => {
    if (!isReady || !twilioClient.current) return

    twilioClient.current.on('conversationAdded', async convresation => {
      const unreadConversationCount = await getConversationUnredMessagesCount(convresation)
      setConversationsUnreadMessagesMap(prev => new Map(prev).set(convresation.sid, unreadConversationCount))
    })

    twilioClient.current.on('messageAdded', async message => {
      if (message.author === twilioClient.current?.user?.identity) return
      const unreadConversationCount = await getConversationUnredMessagesCount(message.conversation)
      setConversationsUnreadMessagesMap(prev => new Map(prev).set(message.conversation.sid, unreadConversationCount))
    })
    twilioClient.current.on('conversationRemoved', getTwilioConversations)
    twilioClient.current.on('userSubscribed', getTwilioConversations)
    twilioClient.current.on('conversationUpdated', getTwilioConversations)
    twilioClient.current.on('participantJoined', getTwilioConversations)
    twilioClient.current.on('participantLeft', getTwilioConversations)
    twilioClient.current.on('connectionError', error => {
      console.log('error', error)
      setError(error)
      setIsReady(false)
    })
    return () => {
      twilioClient.current?.off('conversationAdded', getTwilioConversations)
      twilioClient.current?.off('conversationRemoved', getTwilioConversations)
      twilioClient.current?.off('userSubscribed', getTwilioConversations)
      twilioClient.current?.off('conversationUpdated', getTwilioConversations)
      twilioClient.current?.off('participantJoined', getTwilioConversations)
    }
  }, [isReady, getTwilioConversations, error, getConversationUnredMessagesCount])

  return {
    twilioClient: twilioClient.current,
    conversations,
    createConversation,
    isReady,
    error,
    unreadMessagesTotal,
    conversationsUnreadMessagesMap,
    deleteConversation,
    updateConversationFriendlyName,
    conversationsLoading,
    setConversationsUnreadMessagesMap
  }
}

export { useTwilioConversations }
